import React from 'react';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Loader2, Zap } from 'lucide-react';
import { isChunkLoadError } from '@/utils/chunkErrorHandler';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: Error; resetError: () => void }>;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  isChunkError: boolean;
}

// Chunk error fallback component - auto-reloads the page
const ChunkErrorFallback: React.FC = () => {
  const { t } = useTranslation('common');
  const [countdown, setCountdown] = React.useState(2);

  React.useEffect(() => {
    // Countdown before reload
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          // Reload the page to get fresh HTML with correct chunk references
          window.location.reload();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <>
      {/* Overlay backdrop to keep user connected to the page */}
      <div className='fixed inset-0 z-50 bg-black/80 backdrop-blur-sm' />

      {/* Content */}
      <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
        <div className='max-w-md w-full bg-gray-900 border border-purple-500/20 text-white shadow-lg rounded-lg p-6 text-center'>
          <div className='flex justify-center mb-4 relative'>
            {/* Gaming-themed loader with pulsing effect */}
            <div className='relative'>
              <Zap className='h-12 w-12 text-purple-400 animate-pulse' />
              <Loader2 className='h-12 w-12 text-purple-400 animate-spin absolute inset-0 opacity-30' />
            </div>
          </div>
          <h1 className='text-xl font-semibold text-white mb-2'>
            {t('errorBoundary.syncingVersion')}
          </h1>
          <p className='text-gray-300 mb-4'>
            {t('errorBoundary.newUpdateAvailable', { countdown })}
          </p>
          <Button
            onClick={() => window.location.reload()}
            className='w-full'
            variant='default'
          >
            <Zap className='h-4 w-4 me-2' />
            {t('errorBoundary.reconnectNow')}
          </Button>
        </div>
      </div>
    </>
  );
};

// Default fallback component for non-chunk errors - redirects to home in production
const DefaultErrorFallback: React.FC<{
  error: Error;
  resetError: () => void;
}> = ({ error, resetError }) => {
  const { t } = useTranslation('common');
  // Prefer Vite's DEV flag so `pnpm start` always shows the stack — even when
  // `.env.local` forces `VITE_APP_ENV=production` for analytics.
  const isDev =
    Boolean(import.meta.env.DEV) ||
    import.meta.env.VITE_APP_ENV === 'dev' ||
    import.meta.env.VITE_APP_ENV === 'local';
  const [countdown, setCountdown] = React.useState(3);

  React.useEffect(() => {
    // Only redirect in production
    if (isDev) {
      return;
    }

    // Countdown before redirect
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          // Redirect to home page
          window.location.href = '/';
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isDev]);

  // In development, show error details without redirecting
  if (isDev) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gray-50'>
        <div className='max-w-2xl w-full bg-white shadow-lg rounded-lg p-6'>
          <div className='flex justify-center mb-4'>
            <AlertTriangle className='h-12 w-12 text-red-500' />
          </div>
          <h1 className='text-xl font-semibold text-gray-900 mb-2 text-center'>
            {t('errorBoundary.somethingWentWrong')}
          </h1>
          <p className='text-gray-600 mb-4 text-center'>
            {t('errorBoundary.errorOccurred')}
          </p>
          <details className='mb-4 text-start' open>
            <summary className='cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900 mb-2'>
              {t('errorBoundary.errorDetails')}
            </summary>
            <pre className='mt-2 text-xs text-red-600 bg-red-50 p-4 rounded overflow-auto max-h-96'>
              {error.message}
              {error.stack && `\n\n${error.stack}`}
            </pre>
          </details>
          <div className='space-y-2'>
            <Button onClick={resetError} className='w-full' variant='default'>
              {t('buttons.tryAgain')}
            </Button>
            <Button
              onClick={() => (window.location.href = '/')}
              className='w-full'
              variant='outline'
            >
              {t('errorBoundary.goHome')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // In production, show minimal UI similar to chunk error
  return (
    <>
      {/* Overlay backdrop */}
      <div className='fixed inset-0 z-50 bg-black/80 backdrop-blur-sm' />

      {/* Content */}
      <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
        <div className='max-w-md w-full bg-gray-900 border border-purple-500/20 text-white shadow-lg rounded-lg p-6 text-center'>
          <div className='flex justify-center mb-4 relative'>
            <div className='relative'>
              <Zap className='h-12 w-12 text-purple-400 animate-pulse' />
              <Loader2 className='h-12 w-12 text-purple-400 animate-spin absolute inset-0 opacity-30' />
            </div>
          </div>
          <h1 className='text-xl font-semibold text-white mb-2'>
            {t('errorBoundary.somethingWentWrong')}
          </h1>
          <p className='text-gray-300 mb-4'>
            {t('errorBoundary.redirectingIn', { countdown })}
          </p>
          <Button
            onClick={() => (window.location.href = '/')}
            className='w-full'
            variant='default'
          >
            <Zap className='h-4 w-4 me-2' />
            {t('errorBoundary.goHomeNow')}
          </Button>
        </div>
      </div>
    </>
  );
};

class SentryErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, isChunkError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Check if this is a chunk loading error
    const isChunkError = isChunkLoadError(error);

    return {
      hasError: true,
      error,
      isChunkError,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to Sentry with additional context for chunk errors
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
      tags: {
        isChunkError: isChunkLoadError(error) ? 'true' : 'false',
      },
    });
  }

  resetError = () => {
    this.setState({ hasError: false, error: null, isChunkError: false });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      // If it's a chunk error, show the auto-reload UI
      if (this.state.isChunkError) {
        return <ChunkErrorFallback />;
      }

      // Otherwise, show the normal error boundary UI
      const FallbackComponent = this.props.fallback || DefaultErrorFallback;
      return (
        <FallbackComponent
          error={this.state.error}
          resetError={this.resetError}
        />
      );
    }

    return this.props.children;
  }
}

export default SentryErrorBoundary;
